import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { ConferenceEligibilityService } from 'src/app/core/services/conference-eligibility.service';
import {
  ConferenceApplicationAnswerModel,
  ConferenceApplicationModel,
  ConferenceApplicationSubmitModel,
  ConferenceEligibilityEmployeeViewModel,
  ConferenceOpportunityModel,
  ConferenceOpportunityQuestionModel
} from 'src/app/models/conferenceEligibilityModels';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-conference-application',
  templateUrl: './conference-application.component.html',
  standalone: false
})
export class ConferenceApplicationComponent extends BaseComponent implements OnInit {
  view: ConferenceEligibilityEmployeeViewModel;
  opportunity: ConferenceOpportunityModel;
  model: ConferenceApplicationSubmitModel = {
    levelID: 1,
    requestedTitle: '',
    requestedLocation: '',
    requestedStartDate: '',
    requestedEndDate: '',
    answers: []
  };
  answers: Record<number, any> = {};
  files: Record<number, { fileName: string; fileType: string; fileData: string }> = {};
  loading = true;
  saving = false;
  opportunityUnavailable = false;
  submittedApplication: ConferenceApplicationModel;

  constructor(
    private route: ActivatedRoute,
    private conferenceEligibilityService: ConferenceEligibilityService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.conferenceEligibilityService.getEmployeeView().subscribe({
        next: view => {
          this.view = view;
          const opportunityId = Number(this.route.snapshot.params.opportunityId || 0);
          this.opportunity = view.openOpportunities.find(x => this.getId(x) === opportunityId);
          if (opportunityId && !this.opportunity) {
            this.opportunityUnavailable = true;
            this.alertService.error('This conference opportunity is no longer open for applications.');
            this.loading = false;
            return;
          }
          if (this.opportunity) {
            this.model.opportunityID = opportunityId;
            this.model.levelID = this.opportunity.levelID;
            this.model.requestedTitle = this.opportunity.title;
            this.model.requestedLocation = this.opportunity.location;
            this.model.requestedStartDate = this.toDate(this.opportunity.startDate);
            this.model.requestedEndDate = this.toDate(this.opportunity.endDate);
          }
          this.loading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load conference application.'));
          this.loading = false;
        }
      })
    );
  }

  get questions() {
    return this.opportunity?.questions || [];
  }

  get exceptionReasons() {
    if (!this.view) {
      return [];
    }

    const reasons = [
      ...(this.view.eligibility.levelResults.find(x => x.levelID === Number(this.model.levelID))?.reasons || [])
    ];
    const startDate = this.model.requestedStartDate ? new Date(`${this.model.requestedStartDate}T00:00:00`) : null;
    if (startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const requiredDays = Number(this.model.levelID) === 4
        ? this.view.settings.internationalAdvanceDays
        : this.view.settings.localNationalAdvanceDays;
      const actualDays = Math.floor((startDate.getTime() - today.getTime()) / 86400000);
      if (actualDays < requiredDays) {
        reasons.push(`This request is ${requiredDays - actualDays} day(s) inside the recommended advance notice window.`);
      }
    }

    return [...new Set(reasons)];
  }

  toggleMultipleChoice(questionId: number, option: string, checked: boolean) {
    const current = Array.isArray(this.answers[questionId]) ? [...this.answers[questionId]] : [];
    this.answers[questionId] = checked
      ? [...new Set([...current, option])]
      : current.filter(value => value !== option);
  }

  onFileSelected(question: ConferenceOpportunityQuestionModel, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      delete this.files[this.getId(question)];
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.alertService.error('Application files cannot exceed 10 MB.');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.files[this.getId(question)] = {
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        fileData: reader.result as string
      };
    };
    reader.readAsDataURL(file);
  }

  onSignatureChanged(question: ConferenceOpportunityQuestionModel, fileData: string) {
    const questionId = this.getId(question);
    if (!fileData) {
      delete this.files[questionId];
      return;
    }

    this.files[questionId] = {
      fileName: `signature-${questionId}.png`,
      fileType: 'image/png',
      fileData
    };
  }

  submit() {
    if (!this.model.requestedTitle?.trim()
      || !this.model.requestedStartDate
      || !this.model.requestedEndDate
      || !this.model.levelID) {
      this.alertService.error('Conference title, level, start date, and end date are required.');
      return;
    }

    const missingQuestion = this.questions.find(question => {
      if (!question.isRequired) {
        return false;
      }
      const questionId = this.getId(question);
      if (question.questionTypeID === 7 || question.questionTypeID === 8) {
        return !this.files[questionId]?.fileData;
      }
      const answer = this.answers[questionId];
      return answer === null
        || answer === undefined
        || answer === ''
        || (Array.isArray(answer) && answer.length === 0);
    });
    if (missingQuestion) {
      this.alertService.error(`"${missingQuestion.prompt}" is required.`);
      return;
    }

    const answers: ConferenceApplicationAnswerModel[] = this.questions.map(question => {
      const questionId = this.getId(question);
      const file = this.files[questionId];
      return {
        questionID: questionId,
        questionTypeID: question.questionTypeID,
        answerJson: file ? null : JSON.stringify(this.answers[questionId] ?? null),
        fileName: file?.fileName,
        fileType: file?.fileType,
        fileData: file?.fileData
      };
    });

    this.saving = true;
    this.subs.push(
      this.conferenceEligibilityService.submitApplication({ ...this.model, answers }).subscribe({
        next: application => {
          this.submittedApplication = application;
          this.saving = false;
          this.alertService.success(application.isException
            ? 'Application submitted with exception flags for reviewer attention.'
            : 'Conference application submitted.');
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to submit conference application.'));
          this.saving = false;
        }
      })
    );
  }

  isSelected(questionId: number, option: string) {
    return Array.isArray(this.answers[questionId]) && this.answers[questionId].includes(option);
  }

  getId(value: any) {
    return Number(value?.id ?? value?.iD ?? value?.ID ?? 0);
  }

  private toDate(value: string) {
    return value ? value.substring(0, 10) : '';
  }

  private errorMessage(error: any, fallback: string) {
    return typeof error?.error === 'string'
      ? error.error
      : error?.error?.message || error?.message || fallback;
  }
}
